import { useEffect, useRef, useState } from 'react'
import { CalendarDays, CheckSquare, ExternalLink, Pencil, Send, Trash2, Video, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button, Card, Checkbox, Dropdown, EmptyState, IconButton, Tooltip } from '@/components/ui'

import { formatDayLabel, formatTimecodeOrDash, isOverdueDay } from '@/internationalization/format'
import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'


import { toast } from '@/stores'
import type { ActionItem } from '@/types'
import { ActionItemPatron } from '@/features/patrons'
import { AssigneeLabel, AssigneePicker } from './AssigneePicker'
import {
  useDeleteActionItem,
  useSetActionStatus,
  useUpdateActionItem,
} from '../hooks'
import type { ActionListParams, AssigneePayload } from '../api'

/**
 * Action items as an editable checklist.
 *
 * Three things are never hidden, because each one is a way this product can be
 * confidently wrong: an owner the model guessed at is marked "inferred", an
 * item with nobody on it is marked "Unassigned", and an assignee who was not on
 * the meeting's invite says so — they never heard the commitment being made.
 *
 * Ticking one is optimistic (see useSetActionStatus). Editing the text, the due
 * date or the assignee is NOT: those can be refused for reasons only the server
 * knows, and an assignee that appears and then vanishes reads as the product
 * reassigning the item by itself.
 */
export function ActionItemList({
  items,
  context,
  filterParams = {},
  highlightId,
}: {
  items: ActionItem[]
  /** 'meeting' hides the source meeting; 'workspace' names it. */
  context: 'meeting' | 'workspace'
  /** Passed to the mutation hooks so an optimistic tick patches the right cache entry. */
  filterParams?: ActionListParams
  /**
   * A single item to call out, e.g. the one a notification's deep-link named.
   * Unlike the workspace page's `actionItemId` filter, this list is not
   * narrowed to it — a meeting's Actions tab still shows every item, with this
   * one singled out so a reader can see it in context rather than in
   * isolation.
   */
  highlightId?: string
}) {
  const t = useT()
  const setStatus = useSetActionStatus(filterParams)
  const update = useUpdateActionItem()
  const remove = useDeleteActionItem()
  const [editingId, setEditingId] = useState<string | null>(null)
  const highlightRef = useRef<HTMLLIElement>(null)

  // Scrolls to the highlighted item once, when the list first has it — not on
  // every render, or reopening an edit box on an unrelated item would yank the
  // page back to whichever item the URL named on load.
  useEffect(() => {
    if (highlightId) highlightRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightId, items.length > 0])

  if (items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<CheckSquare />}
          title={t('actions.noItemsTitle')}
          description={t('actions.noItemsBody')}
        />
      </Card>
    )
  }

  return (
    <Card>
      <ul className="divide-y divide-border">
        {items.map((item) => {
          const isDone = item.status === 'done'
          const isOverdue = !isDone && isOverdueDay(item.dueDate)
          const isEditing = editingId === item.id

          const patch = (body: Parameters<typeof update.mutate>[0]['patch']) =>
            update.mutate({ id: item.id, patch: body, rowVersion: item.rowVersion })

          const isHighlighted = item.id === highlightId

          return (
            <li
              key={item.id}
              ref={isHighlighted ? highlightRef : undefined}
              className={cn(
                'flex items-start gap-3 px-3 py-3',
                isHighlighted && 'bg-brand-subtle/40 ring-1 ring-inset ring-brand',
              )}
            >
              <span className="pt-0.5">
                <Checkbox
                  checked={isDone}
                  aria-label={isDone ? t('actions.reopen', { text: item.text }) : t('actions.markDone', { text: item.text })}
                  onChange={(event) =>
                    setStatus.mutate({
                      id: item.id,
                      status: event.target.checked ? 'done' : 'open',
                      rowVersion: item.rowVersion,
                    })
                  }
                />
              </span>

              <div className="min-w-0 flex-1">
                {isEditing ? (
                  <TextEditor
                    initial={item.text}
                    isSaving={update.isPending}
                    onCancel={() => setEditingId(null)}
                    onSave={(text) => {
                      setEditingId(null)
                      if (text !== item.text) patch({ text })
                    }}
                  />
                ) : (
                  <p className={cn('text-base text-fg', isDone && 'text-fg-subtle line-through')}>
                    {item.text}
                  </p>
                )}

                {/* THE SOURCE MEETING, on its own line rather than as the fifth
                    chip in the metadata row below.

                    An action item is a claim about something somebody said, and
                    the meeting is what makes that claim checkable — it is the
                    difference between "chase Priya about the runbook" and a
                    task with no provenance. It carries the timecode because the
                    two are one destination: a moment only means anything inside
                    the recording it is a moment of. On a meeting's own page
                    (`context === 'meeting'`) naming the meeting would be
                    repeating the page title, so only the timecode survives, in
                    the row below. */}
                {context === 'workspace' && (
                  <Link
                    to={`/meetings/${item.meetingId}`}
                    className="mt-1 inline-flex max-w-full items-center gap-1.5 text-sm text-fg-muted hover:text-fg hover:underline"
                  >
                    <Video aria-hidden className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{item.meetingTitle}</span>
                    {item.atSeconds !== null && (
                      <span className="shrink-0 font-mono text-xs text-fg-subtle">
                        {formatTimecodeOrDash(item.atSeconds)}
                      </span>
                    )}
                  </Link>
                )}

                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-fg-muted">
                  <AssigneePicker
                    meetingId={item.meetingId}
                    current={item.assignee}
                    disabled={update.isPending}
                    onSelect={(assignee: AssigneePayload | null) => patch({ assignee })}
                  />
                  {item.assignee && (
                    <AssigneeLabelWithTooltip item={item} />
                  )}
                  {!item.assignee && (
                    <Tooltip content={t('actions.unassignedNobodyTip')}>
                      <span>
                        <AssigneeLabel assignee={null} inferred={false} />
                      </span>
                    </Tooltip>
                  )}

                  <ActionItemPatron
                    patrons={item.patrons}
                    isOverridden={item.patronOverride}
                    disabled={update.isPending}
                    onChange={(patron) => patch({ patron })}
                  />

                  <DueDateEditor
                    value={item.dueDate}
                    isOverdue={isOverdue}
                    isSaving={update.isPending}
                    onChange={(dueDate) => patch({ dueDate })}
                  />

                  {/* A manual item cites no moment in the recording, so there is
                      no timecode to offer — a 0:00 link would send somebody to a
                      point where nothing was said. */}
                  {context === 'meeting' && item.atSeconds !== null && (
                    <Link
                      to={`/meetings/${item.meetingId}`}
                      className="font-mono text-xs text-fg-subtle hover:text-brand"
                    >
                      {formatTimecodeOrDash(item.atSeconds)}
                    </Link>
                  )}

                  {item.origin === 'manual' && (
                    <Tooltip content={t('actions.addedByHandTip')}>
                      <span className="text-xs text-fg-subtle">{t('actions.addedByHand')}</span>
                    </Tooltip>
                  )}

                  {item.pushedTo.map((target) => (
                    <a
                      key={target.provider}
                      href={target.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-brand hover:underline"
                    >
                      {target.provider}
                      <ExternalLink aria-hidden className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                {!isEditing && (
                  <IconButton
                    label={t('actions.edit', { text: item.text })}
                    icon={<Pencil />}
                    size="sm"
                    onClick={() => setEditingId(item.id)}
                  />
                )}
                <Dropdown
                  trigger={({ toggle }) => (
                    <IconButton label={t('actions.more')} icon={<Send />} size="sm" onClick={toggle} />
                  )}
                  items={[
                    { id: 'jira', label: t('actions.createJira'), onSelect: () => toast.success(t('actions.toast.sentToJira')) },
                    { id: 'slack', label: t('actions.postSlack'), onSelect: () => toast.success(t('actions.toast.postedToSlack')) },
                    { id: 'notion', label: t('actions.addNotion'), onSelect: () => toast.info(t('actions.toast.notionNotConnected')) },
                    {
                      id: 'in_progress',
                      label: item.status === 'in_progress' ? t('actions.moveBackToOpen') : t('actions.markInProgress'),
                      separatorBefore: true,
                      onSelect: () =>
                        setStatus.mutate({
                          id: item.id,
                          status: item.status === 'in_progress' ? 'open' : 'in_progress',
                          rowVersion: item.rowVersion,
                        }),
                    },
                    // Dismiss keeps the row and records that a person judged it.
                    // Delete removes it outright and is offered ONLY for an item
                    // somebody typed: an extracted one has usually already gone
                    // out in the summary email, so deleting it would make the
                    // product disagree with what is in people's inboxes.
                    {
                      id: 'dismiss',
                      label: t('actions.dismissItem'),
                      destructive: true,
                      onSelect: () =>
                        setStatus.mutate({ id: item.id, status: 'dismissed', rowVersion: item.rowVersion }),
                    },
                    ...(item.origin === 'manual'
                      ? [
                          {
                            id: 'delete',
                            label: t('actions.deleteItem'),
                            icon: <Trash2 />,
                            destructive: true,
                            onSelect: () => remove.mutate(item.id),
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}

function AssigneeLabelWithTooltip({ item }: { item: ActionItem }) {
  const t = useT()
  if (item.ownerInferred) {
    return (
      <Tooltip content={t('actions.inferredTip')}>
        <span>
          <AssigneeLabel assignee={item.assignee} inferred />
        </span>
      </Tooltip>
    )
  }
  if (item.assignee?.kind === 'external') {
    return (
      <Tooltip content={t('actions.externalTip')}>
        <span>
          <AssigneeLabel assignee={item.assignee} inferred={false} />
        </span>
      </Tooltip>
    )
  }
  return <AssigneeLabel assignee={item.assignee} inferred={false} />
}

function TextEditor({
  initial,
  isSaving,
  onSave,
  onCancel,
}: {
  initial: string
  isSaving: boolean
  onSave: (text: string) => void
  onCancel: () => void
}) {
  const t = useT()
  const [value, setValue] = useState(initial)
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    ref.current?.focus()
    ref.current?.setSelectionRange(initial.length, initial.length)
  }, [initial.length])

  const trimmed = value.trim()
  const commit = () => {
    // An empty edit is a cancel, not a save: the API refuses blank text, and
    // "your change was not saved" for something somebody deliberately cleared
    // reads as a bug rather than as a rule.
    if (!trimmed) return onCancel()
    onSave(trimmed)
  }

  return (
    <div>
      <textarea
        ref={ref}
        value={value}
        rows={2}
        maxLength={2000}
        aria-label={t('actions.editorText')}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            commit()
          }
          if (event.key === 'Escape') onCancel()
        }}
        className="w-full rounded-md border border-border bg-surface-sunken px-2.5 py-1.5 text-base text-fg focus:outline focus:outline-2 focus:outline-brand"
      />
      <div className="mt-1.5 flex items-center gap-2">
        <Button size="sm" onClick={commit} disabled={isSaving || !trimmed}>
          {t('common.save')}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <span className="text-xs text-fg-subtle">{t('actions.editorHint')}</span>
      </div>
    </div>
  )
}

/**
 * The due date, edited in place.
 *
 * A native date input rather than a custom calendar: it is keyboard- and
 * screen-reader-complete, it speaks the viewer's locale, and a due date is a
 * DAY — the API stores a date, so there is no time to pick and no zone to get
 * wrong. Clearing it is a first-class action, because "no deadline" is a real
 * state and the alternative is somebody inventing one.
 */
function DueDateEditor({
  value,
  isOverdue,
  isSaving,
  onChange,
}: {
  value: string | null
  isOverdue: boolean
  isSaving: boolean
  onChange: (dueDate: string | null) => void
}) {
  const t = useT()
  const [isEditing, setIsEditing] = useState(false)
  const asDay = value ? value.slice(0, 10) : ''

  if (isEditing) {
    return (
      <span className="inline-flex items-center gap-1">
        <input
          type="date"
          autoFocus
          defaultValue={asDay}
          aria-label={t('actions.dueDate')}
          disabled={isSaving}
          onBlur={() => setIsEditing(false)}
          onChange={(event) => {
            const day = event.target.value
            setIsEditing(false)
            // Midnight UTC, matching what the API sends back for a date column.
            onChange(day ? `${day}T00:00:00Z` : null)
          }}
          className="rounded-md border border-border bg-surface-sunken px-1.5 py-0.5 text-sm text-fg focus:outline focus:outline-2 focus:outline-brand"
        />
        {value && (
          <IconButton
            label={t('actions.clearDueDate')}
            icon={<X />}
            size="sm"
            onClick={() => {
              setIsEditing(false)
              onChange(null)
            }}
          />
        )}
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 hover:bg-surface-raised hover:text-fg',
        isOverdue && 'font-medium text-danger',
      )}
    >
      <CalendarDays aria-hidden className="h-3.5 w-3.5" />
      {value ? (
        <>
          {isOverdue ? t('actions.overduePrefix') : t('actions.duePrefix')}
          {formatDayLabel(value)}
        </>
      ) : (
        t('actions.setDueDate')
      )}
    </button>
  )
}
